import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  LOCALE_LABELS,
  PICKABLE_LOCALES,
  setLocale,
} from '../lib/i18n';
import type { AnyLocale } from '@swiss-magic/i18n';

// Story 070b — user-facing language picker. Only rendered for
// authenticated users in the AppHeader (next to the user-menu).
// Anonymous users don't get a picker on purpose: the boot chain
// auto-detects their navigator.language and we don't want a picker
// choice on a shared browser to bleed into the next visitor's
// session.
//
// Native-language labels (`English`, `Polski`, `Deutsch`) come from
// LOCALE_LABELS in lib/i18n.ts — kept out of the translation catalog
// on purpose: a user who can't read the current language still needs
// to find their own language in the dropdown.

interface LocalePickerProps {
  /**
   * Compact variant renders the trigger as a single Globe icon
   * without a label (used in the header where space matters).
   */
  compact?: boolean;
}

export function LocalePicker({ compact = false }: LocalePickerProps) {
  const { i18n, t } = useTranslation('common');
  // AuthProvider wraps every route that mounts this component
  // (authenticated routes only); no need to guard against missing
  // context.
  const { session } = useAuth();
  // i18n.language is reactive via the languageChanged event that
  // react-i18next subscribes to, but only inside components that
  // call useTranslation (which we do above). Mirror to local state
  // so the dropdown indicator updates without re-renders elsewhere.
  const [current, setCurrent] = useState<string>(i18n.language);
  useEffect(() => {
    const cb = (lng: string) => setCurrent(lng);
    i18n.on('languageChanged', cb);
    return () => i18n.off('languageChanged', cb);
  }, [i18n]);

  async function handlePick(locale: AnyLocale) {
    await setLocale(locale, {
      accessToken: session?.access_token ?? null,
    });
  }

  const currentLabel =
    LOCALE_LABELS[current as AnyLocale] ?? LOCALE_LABELS.en;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('header.user_menu.language')}
          data-testid="locale-picker"
          className={compact ? 'gap-1' : 'gap-2'}
        >
          <Globe className="h-4 w-4" />
          <span
            data-testid="locale-picker-current"
            className={compact ? 'sr-only' : ''}
          >
            {currentLabel}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="locale-picker-menu">
        {PICKABLE_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            data-testid={`locale-option-${locale}`}
            onClick={() => void handlePick(locale)}
            className="justify-between gap-4"
          >
            <span>{LOCALE_LABELS[locale]}</span>
            {current === locale && (
              <Check className="h-4 w-4 text-muted-foreground" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

